import { useContext, useState } from 'react'
import { Space, Table, Button, Typography, Badge } from 'antd'
import { useQuery } from 'react-query'
import { formatDate, formatNb } from '../../utils/utils'

import { getCustomerGiftCards } from '../../network/GiftCards'
import { AppContext } from '../../context/AppContext'

import DynamicStrings from '../../components/DynamicStrings'

const { Text } = Typography

const CustomersGiftCards = ({ userId, user, history }) => {
    const { token, user: userR } = useContext(AppContext)
    const [activeSort, setActiveSort] = useState(['createdAt', 'DESC'])
    const [usedSort, setUsedSort] = useState(['createdAt', 'DESC'])

    const active = useQuery(
        [
            'getCustomerGiftCards',
            {
                userId,
                filter: { status: 'validated' },
                token: token.token,
                sort: activeSort,
            },
        ],
        getCustomerGiftCards
    )

    const used = useQuery(
        [
            'getCustomerGiftCards',
            {
                userId,
                filter: { status: 'used' },
                token: token.token,
                sort: usedSort,
            },
        ],
        getCustomerGiftCards
    )

    const handleActiveChange = (pagination, filters, sorter) => {
        if (sorter.order) {
            setActiveSort([
                sorter.columnKey,
                sorter.order === 'ascend' ? 'ASC' : 'DESC',
            ])
        } else {
            setActiveSort(['createdAt', 'DESC'])
        }
    }

    const handleUsedChange = (pagination, filters, sorter) => {
        if (sorter.order) {
            setUsedSort([
                sorter.columnKey,
                sorter.order === 'ascend' ? 'ASC' : 'DESC',
            ])
        } else {
            setUsedSort(['createdAt', 'DESC'])
        }
    }

    return (
        <Space
            direction="vertical"
            size={15}
            style={{ width: '100%', paddingBottom: 24 }}
        >
            <div className="customer-voucher-button-wrap">
                {userR.role !== 'logistic' && (
                    <Button
                        onClick={() =>
                            history.push({
                                pathname: '/gift-cards/create',
                                customer: user,
                            })
                        }
                        type="primary"
                    >
                        <DynamicStrings id="GIFT_CARDS_CREATE" />
                    </Button>
                )}
            </div>
            <div>
                <div className="customer-voucher-title">
                    <Text>
                        <DynamicStrings id="CUSTOMERS_ACTIVE_GIFT_CARDS" />
                    </Text>
                </div>
                <Table
                    rowKey="id"
                    dataSource={active.data?.list}
                    columns={activeColumns}
                    loading={active.isLoading}
                    className="tableWrap"
                    style={{
                        padding: '0px 24px',
                    }}
                    onChange={handleActiveChange}
                    pagination={false}
                />
            </div>
            <div>
                <div className="customer-voucher-title">
                    <Text>
                        <DynamicStrings id="CUSTOMERS_USED_GIFT_CARDS" />
                    </Text>
                </div>
                <Table
                    rowKey="id"
                    dataSource={used.data?.list}
                    columns={usedColumns}
                    loading={used.isLoading}
                    style={{
                        padding: '0px 24px',
                    }}
                    onChange={handleUsedChange}
                    className="tableWrap"
                    pagination={false}
                />
            </div>
        </Space>
    )
}

export default CustomersGiftCards

const baseColumns = [
    {
        title: <DynamicStrings id="GIFT_CARDS_GIFT_CARD_ID" />,
        dataIndex: 'giftCardID',
        key: 'giftCardID',
        ellipsis: true,
    },
    {
        title: <DynamicStrings id="GIFT_CARDS_TYPE" />,
        dataIndex: 'type',
        render: (type) => <DynamicStrings id={type} />,
        key: 'type',
        sorter: true,
    },
    {
        title: <DynamicStrings id="GIFT_CARDS_GRANTED_BY" />,
        dataIndex: 'grantedBy',
        render: (grantedBy) => grantedBy?.name,
        key: 'grantedBy',
        sorter: true,
    },
    {
        title: <DynamicStrings id="GIFT_CARDS_OFFERED_BY" />,
        dataIndex: 'offeredBy',
        render: (offeredBy) => offeredBy?.name,
        key: 'offeredBy',
        sorter: true,
    },
    {
        title: <DynamicStrings id="amount" suffix="(¥)" />,
        dataIndex: 'amount',
        render: (amount) => formatNb(amount),
        key: 'amount',
        sorter: true,
    },
    {
        title: <DynamicStrings id="ROLES_COMMUNITY" />,
        dataIndex: 'communities',
        render: (communities) => (
            <span>
                {communities?.map((com, i) => (
                    <span key={com.id}>
                        <DynamicStrings id={com.name} />
                        {i < communities.length - 1 && ', '}
                    </span>
                ))}
            </span>
        ),
        key: 'communities',
        sorter: true,
    },
    {
        title: <DynamicStrings id="ZIKO_SPECIALS_EXPIRATION_DATE" />,
        dataIndex: 'expirationDate',
        render: (date) => (date ? formatDate(date) : null),
        key: 'expirationDate',
        sorter: true,
    },
    {
        title: <DynamicStrings id="ZIKO_SPECIALS_CREATION_DATE" />,
        dataIndex: 'createdAt',
        render: (date) => (date ? formatDate(date) : null),
        key: 'createdAt',
        sorter: true,
    },
]

const activeColumns = [
    ...baseColumns,
    {
        title: <DynamicStrings id="OFFERS_STATUS" />,
        dataIndex: 'status',
        render: (status) => (
            <Badge
                color="green"
                text={<DynamicStrings id={status} />}
            />
        ),
        key: 'status',
        sorter: true,
    },
]

const usedColumns = [
    ...baseColumns,
    {
        title: <DynamicStrings id="GIFT_CARDS_USED_AT" />,
        dataIndex: 'usedAt',
        render: (date) => (date ? formatDate(date) : null),
        key: 'usedAt',
        sorter: true,
    },
    {
        title: <DynamicStrings id="GIFT_CARDS_ORDER_USED_ON" />,
        dataIndex: 'orderUsedOn',
        render: (order) => order?.orderID,
        key: 'orderUsedOn',
        sorter: true,
    },
]
